/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from "react";
import { useAppSelector } from "../../../../app/store";
import CustomSegment from "../../../../components/ui/CustomSegment";
import { useGetUserWalletRoleByUserIdQuery } from "../../../../features/portal/companyOnboard/companyOnboardApi";
import useError from "../../../../hooks/useError";
import { useSegment } from "../../../../hooks/useSegment";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import BatchV2 from "./BatchV2";

type SegmentType = "maker" | "checker" | "approver";

export default function BatchManagement() {
  const { user } = useAppSelector((state) => state.auth);

  const { data, isError, error } = useGetUserWalletRoleByUserIdQuery(
    {
      id: Number(user?.data?.id),
      page: 1,
      limit: 100000,
    },
    { skip: !user?.data?.id }
  );

  useError(isError, error, "Get User Wallet Role By User Id failed");

  // Get role list
  const roleList = data?.data?.walletRoles?.map((item) => item.role) || [];

  // Maker Role Data
  const makerRolesData = data?.data?.walletRoles?.filter(
    (item) => item.role === "maker"
  );

  // Checker Role Data
  const checkerRolesData = data?.data?.walletRoles?.filter(
    (item) => item.role === "checker"
  );

  // Approver Role Data
  const approverRolesData = data?.data?.walletRoles?.filter(
    (item) => item.role === "approver"
  );

  // --- Setup segment hook (default "maker")
  const { segment, setSegment } = useSegment<SegmentType>(
    "batch-segment",
    "maker"
  );

  // --- Build config dynamically (memoized)
  const SEGMENT_CONFIG = useMemo(() => {
    const config = [];

    if (roleList.includes("maker")) {
      config.push({
        key: "maker",
        label: "Maker",
        element: <BatchV2 walletRole="maker" walletRoleData={makerRolesData} />,
      });
    }

    if (roleList.includes("checker")) {
      config.push({
        key: "checker",
        label: "Checker",
        element: (
          <BatchV2 walletRole="checker" walletRoleData={checkerRolesData} />
        ),
      });
    }

    if (roleList.includes("approver")) {
      config.push({
        key: "approver",
        label: "Approver",
        element: (
          <BatchV2 walletRole="approver" walletRoleData={approverRolesData} />
        ),
      });
    }

    return config;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleList]); // ✅ rebuild only when roleList changes

  // --- Ensure currentSegment always valid
  useEffect(() => {
    if (roleList.length > 0) {
      // if current segment is invalid, reset to first available role
      const validKeys = SEGMENT_CONFIG.map((s) => s.key);
      if (!validKeys.includes(segment)) {
        // setSegment(validKeys[0] as SegmentType);
        setSegment(validKeys[validKeys.length - 1] as SegmentType);
      }
    }
  }, [roleList, segment, setSegment, SEGMENT_CONFIG]);

  // --- Find active segment element
  const currentSegment = SEGMENT_CONFIG.find((s) => s.key === segment);

  return (
    <>
      <SidebarLayout title="Batch Details">
        <CustomSegment
          config={SEGMENT_CONFIG}
          selected={segment}
          onChange={(value) => setSegment(value as SegmentType)}
        />
        <div className="mt-5">{currentSegment?.element}</div>
      </SidebarLayout>
    </>
  );
}
